import * as Yup from "yup";
import { EntityType } from "../../modules/Onboarding/types";
import {
  TAX_ID_CONFIG,
  TaxIdValidation,
  US_BLOCKED_EIN_PREFIXES,
  US_BLOCKED_INDIVIDUAL_TAX_IDS
} from "../constants/taxId";
import { selectorCountryName } from "../selectors/selectorCountryName";

const US_COUNTRY_CODE = "US";

function createGenericValidation(
  country: string
): Record<EntityType, TaxIdValidation> {
  const countryName = selectorCountryName(country) || country;
  return {
    Individual: {
      regex: /^[A-Z0-9]{6,12}$/,
      errorMessage: `Please enter a valid ${countryName} Tax ID for individuals. It should be 6-12 alphanumeric characters.`,
      name: `${countryName} Individual Tax ID`,
      description: `Tax identification number for individuals in ${countryName}.`,
      placeholder: "ABC123456789"
    },
    Business: {
      regex: /^[A-Z0-9]{6,15}$/,
      errorMessage: `Please enter a valid ${countryName} Tax ID for businesses. It should be 6-15 alphanumeric characters.`,
      name: `${countryName} Business Tax ID`,
      description: `Tax identification number for businesses in ${countryName}.`,
      placeholder: "BUS123456789012"
    }
  };
}

function validateUsBlocklists(
  type: EntityType,
  taxId: string
): { isValid: boolean; errorMessage?: string } {
  const digits = taxId.replace(/\D/g, "");

  if (type === "Individual" && US_BLOCKED_INDIVIDUAL_TAX_IDS.includes(digits)) {
    return {
      isValid: false,
      errorMessage:
        "This number cannot be used as an SSN or ITIN. Please check your entry."
    };
  }

  if (
    type === "Business" &&
    US_BLOCKED_EIN_PREFIXES.includes(digits.slice(0, 2))
  ) {
    return {
      isValid: false,
      errorMessage:
        "The first two digits are not a valid EIN prefix. Please check your entry."
    };
  }

  return { isValid: true };
}

function validateTaxId(
  country: string,
  type: EntityType,
  taxId: string
): { isValid: boolean; errorMessage?: string } {
  const countryValidations =
    TAX_ID_CONFIG[country] || createGenericValidation(country);
  const validation = countryValidations[type];

  if (!validation?.regex.test(taxId)) {
    return { isValid: false, errorMessage: validation?.errorMessage };
  }

  if (country === US_COUNTRY_CODE) {
    return validateUsBlocklists(type, taxId);
  }

  return { isValid: true };
}

export const validatorTaxId = (
  fieldName: string,
  type: EntityType,
  meCountry?: string
) =>
  Yup.string().test(fieldName, "Not valid", function (formTaxIdValue, context) {
    // This validator should not check for value presence
    if (!formTaxIdValue) {
      return true;
    }

    const _country = meCountry || context.parent?.country;
    const taxIdValidation = validateTaxId(_country, type, formTaxIdValue);
    if (!taxIdValidation.isValid) {
      return this?.createError({ message: taxIdValidation.errorMessage });
    }
    return true;
  });
